import ActivityManager from "./../core/activityManager";
import My from "./activityMyAPI";
import { nodeify } from "./../helpers/promiseExtensions";
import { ActivityManagementAPI, ManagementAPI } from "./activityManagementAPI";
import { ActivityAGM } from "../core/activityAGM";

export interface API {

  aware: boolean;

  inActivity: boolean;

  my: My;

  agm: ActivityAGM;

  all: ManagementAPI;

  ready(callback?: (api: API) => void): Promise<API>;

  getAvailableFrameColors(): string[];
}

/**
 * The entry points of activities API - accessible from glue.activities
 *
 * @module activities
 */
export class ActivityAPI implements API {

  public _mgr: ActivityManager;
  public _my: My;

 /**
  * Extended API that provides access to all running activities and windows and available activity and window types
  *
  * @var {module:all} all
  */
 public all: ManagementAPI;

  constructor(manager: ActivityManager, my: My) {
    this._mgr = manager;
    this._my = my;
    this.all = new ActivityManagementAPI(manager, my);
  }

  /**
   * The entry point for your activity code.
   *
   * @function ready
   * @returns {Promise<module:activities>} A promise for activity API
   */
  public ready(callback?: any): Promise<API> {
    const promise = new Promise<any>((resolve, reject) => {
      this._mgr.ready()
        .then(() => {
          resolve(this);
        })
        .catch((err) => {
          reject(err);
        });
    });
    return nodeify(promise, callback);
  }

  /**
   * A lightweight, single activity oriented subset of the API which should be used by most activity applications
   *
   * @var {module:my} my
   */
  public get my(): My {
    return this._my;
  }

  /**
   * True if the current window is activity aware - meaning that the window has been created as
   * an activity participant - either a helper or an independent window.
   * Check this after the API is ready.
   *
   * @var {bool} aware
   */
  public get aware(): boolean {
    return this._my.window !== undefined;
  }

  /**
   * Returns true if the current window is activity aware and is currently participating in activity
   *
   * @var {bool} inActivity
   */
  public get inActivity(): boolean {
    return this.aware && this._my.activity !== undefined;
  }

  /**
   * Defines an Activity-specific version of the AGM API which:
   * * restricts method registrations to activity-only windows
   * * restricts method discovery and invocation to activity-only methods, unless you explicitly specify otherwise
   * Other than that, the API is pretty much the same.
   *
   * @var  {module:activityAGM} agm
   *
   */
  public get agm(): ActivityAGM {
    if (!this.aware) {
      // no agm if not activity aware
      return undefined;
    }
    if (!this.inActivity) {
      // if we're not in activity no
      return new ActivityAGM(null);
    }

    return this._my.activity.agm;
  }

  /**
   * Returns a list of frame colors that can be used as activity frame colors
   *
   * @returns {string[]}
   */
  public getAvailableFrameColors(): string[] {
    // TODO - not available in GD3
    return [];
  }
}
